import { KeycloakAuthorizationInstance } from "keycloak-js/dist/keycloak-authz";
import React, {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { PropertyAPIPromiseClient } from "../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import {
  GetPropertyInfoRequest,
  GetPropertyInfoResponse,
} from "../gen/witbooking/backoffice/property/v1/property_api_pb";
import { ACCESS_DENIED, getPropertyRpt } from "../lib/auth";
import { useGrpcClient } from "../lib/grpc";
import { KeycloakRPTSingleton, useKeycloakAuthz } from "./keycloak-provider";
import { PropertyLocalesProvider } from "./property-locale-provider";

export interface PropertyProviderProps {
  propertyTicker: string;
  loader: ReactNode;
}

export interface IProperty {
  ticker: string;
  token: string;
  info: GetPropertyInfoResponse.AsObject;
  /**
   * Forces a fresh RPT exchange with Keycloak for this property (bypassing whatever the current
   * one is, expired or not) and updates this context so every consumer reading `property.token`
   * picks up the new value on its next render. Also returns the new token directly, so a caller
   * that needs to retry an in-flight request right away doesn't have to wait for / rely on that
   * re-render — see fetchWithTokenRefresh (ai-assistant), the actual caller of this today.
   */
  refreshToken: () => Promise<string>;
}

const messages = defineMessages({
  propertyAccessDenied: {
    id: "global.propertyAccessDenied",
    defaultMessage: "You do not have access to this property.",
  },
});

const PropertyContext = React.createContext<IProperty | undefined>(undefined);

export async function getMultiplePropertyRPT(
  propertyTickerList: string[],
  keycloakAuthz: KeycloakAuthorizationInstance
) {
  try {
    const rptPromises = propertyTickerList.map((property: string) => {
      return getPropertyRpt(property, keycloakAuthz);
    });

    const rptList = await Promise.all(rptPromises);

    if (Array.isArray(rptList)) {
      return rptList.map((token, index) => ({ propertyTicker: propertyTickerList[index], token }));
    }
  } catch (e) {
    if (e === ACCESS_DENIED) {
      return;
    } else {
      console.error("Error retrieving rpt", e);
      return;
    }
  }
}

export function PropertyProvider({
  propertyTicker,
  loader,
  children,
}: PropsWithChildren<PropertyProviderProps>) {
  const [value, setValue] = useState<IProperty | undefined>();
  const [status, setStatus] = useState<"loading" | "ok" | "denied" | "error">("loading");
  const keycloakAuthz = useKeycloakAuthz();
  const grpcClient = useGrpcClient(PropertyAPIPromiseClient);

  // Stable across re-renders (identity only changes if propertyTicker/keycloakAuthz themselves
  // do) — safe for memoized consumers (e.g. the chat transport) to depend on without being
  // forced to recreate on every render. Forces a REAL re-exchange (getPropertyRpt always resets
  // authz.rpt first), never serves a stale/cached token back.
  const refreshToken = useCallback(async (): Promise<string> => {
    const token = await getPropertyRpt(propertyTicker, keycloakAuthz);
    KeycloakRPTSingleton.rpt = token;
    setValue(prev => (prev ? { ...prev, token } : prev));
    return token;
  }, [propertyTicker, keycloakAuthz]);

  useEffect(() => {
    async function exec() {
      try {
        setStatus("loading");
        const token = await getPropertyRpt(propertyTicker, keycloakAuthz);
        KeycloakRPTSingleton.rpt = token;
        const propertyInfo = await grpcClient.getPropertyInfo(new GetPropertyInfoRequest(), {
          Authorization: `Bearer ${token}`,
          "x-property": propertyTicker,
        });
        setValue({
          ticker: propertyTicker,
          token: token,
          info: propertyInfo.toObject(),
          refreshToken,
        });
        setStatus("ok");
      } catch (e) {
        if (e === ACCESS_DENIED) {
          setStatus("denied");
        } else {
          console.error("Error retrieving rpt", e);
          setStatus("error");
        }
      }
    }

    // noinspection JSIgnoredPromiseFromCall
    exec();
  }, [keycloakAuthz, propertyTicker, grpcClient, refreshToken]);

  return (
    <>
      {status === "loading" && loader}
      {status === "ok" && (
        <PropertyContext.Provider value={value}>
          <PropertyLocalesProvider locales={value?.info.localesList || []}>
            {children}
          </PropertyLocalesProvider>
        </PropertyContext.Provider>
      )}
      {status === "denied" && <FormattedMessage {...messages.propertyAccessDenied} />}
    </>
  );
}

export function useProperty(): IProperty {
  const property = useContext(PropertyContext);
  if (property == null) {
    throw new Error("You must call useProperty only when inside a PropertyProvider.");
  }
  return property;
}

export function usePropertyOptional(): IProperty | undefined {
  return useContext(PropertyContext);
}

//This one is for compatibility with witadmin
export function withProperty<P = {}>(
  Component: React.ComponentType<P & { property: IProperty }>
): React.ComponentType<P> {
  return (props: P) => {
    const data = useProperty();
    return <Component property={data} {...props} />;
  };
}
